import { Callable } from '@prophecy/interfaces/generic';
import { MonacoEditorClass } from '@prophecy/ui-v3/Editor/utils';
import { toast } from '@prophecy/ui-v3/LegacyToast';
import { isCommentNodeEditorActive } from '@prophecy/ui-v3/MarkdownEditor/utils';
import { afterAnimationFrame } from '@prophecy/utils/dom';
import { KeyCodes } from '@prophecy/utils/keyboard';
import { useLatestRef, usePersistentCallback } from '@prophecy/utils/react/hooks';
import { castArray, forEachRight } from 'lodash-es';
import { nanoid } from 'nanoid';
import { useEffect } from 'react';

import { HistoryManager } from '..';
import { useHistory } from '../context';
import {
  GenericHistoryEntryType as H,
  BaseHistoryEntryType as I,
  GenericHistoryStackEntry as S,
  HistoryEntry,
  HistoryType,
  OnActionHandler
} from '../types';

export const SECOND_ITERATION_DELAY = 100;

const handleResetToast = (resetMessage: string | undefined) => {
  // oxlint-disable-next-line no-unused-expressions
  resetMessage && toast.info({ content: resetMessage });
};

type InternalHistoryManager<T extends I> = Omit<HistoryManager<T>, 'lastChange' | 'nextChange'> & {
  readonly lastChange: () => S<T> | undefined;
  readonly nextChange: () => S<T> | undefined;
};

export function useHistoryHandlers<T extends I>(
  onHistoryChange: OnActionHandler<T>,
  onRedo?: OnActionHandler<T>,
  handleKeyEvents?: boolean
) {
  const history = useHistory() as unknown as InternalHistoryManager<T>;
  const handleKeyEventsRef = useLatestRef(handleKeyEvents);

  const handleHistory = usePersistentCallback(
    (handler: OnActionHandler<T>, value: H<T>['value'], item: HistoryEntry<H<T>>) => {
      const handled = handler(value, item);
      if (handled) return;

      switch (item.metadata.type) {
        case HistoryType.callback: {
          // value will be a fn or fn[] if the type of history event is callback
          castArray(value as Callable | undefined).forEach((f) => f?.());
          break;
        }
      }
    }
  );

  const undo = usePersistentCallback(() => {
    const item = history?.lastChange();
    if (item) {
      forEachRight(item.entries, (entry: HistoryEntry<H<T>> | string) => {
        if (typeof entry !== 'object' || !entry.metadata?.type) return;
        handleHistory(onHistoryChange, entry.prevValue, entry);
      });
    } else handleResetToast(history?.resetStack);
  });

  const redo = usePersistentCallback(() => {
    const item = history?.nextChange();
    if (item) {
      item.entries.forEach((entry: HistoryEntry<H<T>> | string) => {
        if (typeof entry !== 'object' || !entry.metadata?.type) return;
        handleHistory(onRedo || onHistoryChange, entry.nextValue, entry);
      });
    }
  });

  useEffect(() => {
    let prevValue: Record<string, string> = {};
    let inProgress = false;

    // If focussed in an input/textarea/contenteditable, let the browser handle undo first.
    // We detect whether the browser actually did something via two signals:
    //   1. `beforeinput` with inputType 'historyUndo' | 'historyRedo' (primary — native undo path)
    //   2. value/innerText changed across one animation frame (fallback — rich editors like Slate
    //      preventDefault the key and reconcile DOM themselves without dispatching `beforeinput`)
    // If neither fires, the browser/editor had nothing to undo; we take over with the app-level undo.
    const handleUndo = (element: HTMLInputElement, isShiftKey: boolean, isSecondIteration: boolean, id = nanoid()) => {
      inProgress = true;
      const isElementCommentNodeEditor = isCommentNodeEditorActive();
      const value = isElementCommentNodeEditor ? element.innerText : element.value;
      const tagName = element.tagName;

      // For native text inputs and the comment editor, capture the current value and retry
      // once after the browser/editor has had time to process the shortcut.
      if (!isSecondIteration && (tagName === 'TEXTAREA' || tagName === 'INPUT' || isElementCommentNodeEditor)) {
        prevValue[id] = value;

        // if we get browser signal early, then on schedule we don't need to check for value
        let browserHandled = false;
        const onBeforeInput = (ev: Event) => {
          const inputType = (ev as InputEvent).inputType;
          if (inputType === 'historyUndo' || inputType === 'historyRedo') {
            browserHandled = true;
            inProgress = false;
            delete prevValue[id];
          }
        };
        element.addEventListener('beforeinput', onBeforeInput, { capture: true, once: true });

        /**
         * Monaco may enqueue follow-up work on the next animation frame after undo/redo.
         * Waiting for that frame avoids racing with editor cleanup.
         * Regular inputs get a short timeout so the DOM value needs time to settle, so wait little longer
         */
        const schedule = element.closest(`.${MonacoEditorClass}`)
          ? afterAnimationFrame
          : (cb: () => void) => setTimeout(cb, SECOND_ITERATION_DELAY);

        schedule(() => {
          element.removeEventListener('beforeinput', onBeforeInput, { capture: true });
          if (browserHandled) return;
          handleUndo(element, isShiftKey, true, id);
        });
        return false;
      }

      // On the retry pass, only fall back to app history if the value did not change.
      inProgress = false;
      if (isSecondIteration) {
        const didChange = prevValue[id] !== value;
        delete prevValue[id];
        if (didChange) return false;
        element.blur();
      }

      if (isShiftKey) {
        redo();
      } else {
        undo();
      }
      return true;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        (e.key?.toLowerCase() === KeyCodes.Z && (e.ctrlKey || e.metaKey)) ||
        (e.key?.toLowerCase() === KeyCodes.Y && e.ctrlKey)
      ) {
        if (
          handleKeyEventsRef.current === false ||
          document.body.dataset.hasBlockingDialog ||
          history?.blocked ||
          (!inProgress &&
            handleUndo(e?.target as HTMLInputElement, e.key?.toLowerCase() === KeyCodes.Y || e.shiftKey, false))
        ) {
          // if the undo is handled through history manager, then prevent the default behavior
          e.preventDefault();
          e.stopPropagation();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [handleKeyEventsRef, redo, undo, history]);

  return { undo, redo };
}
